import MultiSelect from '../../../shared/multi-select/multi-select';
import React, {useEffect, useMemo, useState} from 'react';
import {ConnectionModel} from '../connection.model';
import './ecm-filters.scss';
import {observer} from 'mobx-react-lite';
import {useStores} from '../../../store';
import FilterInput from '../../../shared/filters/filter-input/filter-input';
import {CircuitType, getCircuitTypeDisplayLabel, VersionLabel} from '@src/utils/enums';
import {EmailLinkSource} from '@src/dtos/analytics.dto';

interface EcmFiltersProps {
    setData: (data?: ConnectionModel[]) => void;
    setSelectedConnections: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>
}

interface MultiselectFilterProps {
    label: string;
    options: { label: string; value: string }[];
    selectedList: string[];
    onSelectionChange: (selected: string[]) => void;
}

const MultiselectFilter: React.FC<MultiselectFilterProps> = ({ label, options, selectedList, onSelectionChange }) => (
    <span className="multiselect-filter-wrapper">
        <span className="multiselect-filter-label">{label}</span>
        <MultiSelect
            className="multiselect-filter"
            options={options}
            selectedList={selectedList}
            onSelectionChange={onSelectionChange}
            showAllOption={true}
        />
    </span>
);

const EcmFilters: React.FC<EcmFiltersProps> = observer(({ setData, setSelectedConnections }) => {
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [planFilter, setPlanFilter] = useState<string[]>([]);
    const [searchText, setSearchText] = useState<string>('');

    type StatusCount = Record<VersionLabel, number>;

    const {customerStore, uiStore} = useStores();
    const { connectionList } = customerStore;

    const [statusFilter, setStatusFilter] = useState<'All' | VersionLabel>(
        uiStore.emailLinkSource === EmailLinkSource.SEVEN_DAYS_NO_APPLY ? VersionLabel.Initial : 'All');

    const statusCount: StatusCount = useMemo(() =>
        connectionList?.reduce((acc, connection) => {
            const status = connection.policyStatus;
            if(status) {
                if (!acc[status]) {
                    acc[status] = 0;
                }
                acc[status]++;
            }
            return acc;
        }, {} as StatusCount) || {} as StatusCount, [connectionList]);

    const hasInitials = useMemo(() =>
        !!connectionList?.filter(_=>_.policyStatus===VersionLabel.Initial).length,
    [connectionList]);

    const serviceTypeOptions = [
        { label: getCircuitTypeDisplayLabel(CircuitType.ADI), value: CircuitType.ADI },
        { label: getCircuitTypeDisplayLabel(CircuitType.ASEoD), value: CircuitType.ASEoD },
        { label: getCircuitTypeDisplayLabel(CircuitType.ABF), value: CircuitType.ABF },
        { label: getCircuitTypeDisplayLabel(CircuitType.ABF_EI), value: CircuitType.ABF_EI },
        { label: getCircuitTypeDisplayLabel(CircuitType.AIA_B), value: CircuitType.AIA_B },
        ...(connectionList?.some(connection => connection.type === CircuitType.MOBILE_CRU) ?
            [{ label: getCircuitTypeDisplayLabel(CircuitType.MOBILE_CRU), value: CircuitType.MOBILE_CRU }] : []),
    ];
    const servicePlanOptions = ['Standard', 'Advanced', 'Premium'].map(plan => ({ label: plan, value: plan.toUpperCase() }));
    const serviceStatusOptions = [
        'All',
        ...(hasInitials ? [VersionLabel.Initial] : []),
        VersionLabel.Draft,
        VersionLabel.Inactive
    ].map(status => ({ label: status, value: status as 'All' | VersionLabel }));

    const filter = (data: ConnectionModel[]) => {
        return data.filter((connection: ConnectionModel) => {
            if (typeFilter.length !== 0 && !typeFilter.includes(connection.type))
                return false;

            if (planFilter.length !== 0 && !planFilter.includes(connection.plan))
                return false;

            if (statusFilter !== 'All' && statusFilter !== connection.policyStatus)
                return false;

            if (!searchText || searchText?.length === 0)
                return true;

            return connection.connectionName?.toLowerCase().includes(searchText.toLowerCase()) ||
                connection.circuitId.toLowerCase().includes(searchText.toLowerCase());
        });
    };

    const filteredData = useMemo(() =>
        connectionList ? filter(connectionList) : undefined
    ,[connectionList, typeFilter, planFilter, statusFilter, searchText]);

    useEffect(() => {
        setData(filteredData);
    }, [filteredData]);

    useEffect(() => {
        setSelectedConnections(new Map());
    }, [typeFilter, planFilter, statusFilter]);

    return (
        <div className="ecm-filters">
            <div className="data-count">
                <span className="label">Connections</span>
                <span className="count"> ({filteredData?.length !== connectionList?.length ?
                    `${filteredData?.length}/${connectionList?.length}` : connectionList?.length})</span>
            </div>
            <FilterInput
                placeholder={'Search by name/circuit ID'}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
            />
            <div className="status-filter-wrapper">
                {serviceStatusOptions.map(option => {
                    const isNotClickable = option.value !== 'All' && !statusCount[option.value];
                    return (
                        <button type="button" key={option.value}
                                disabled={isNotClickable}
                                className={`status-filter ${option.value === statusFilter ? 'active' : ''}`}
                                onClick={() => setStatusFilter(option.value)}>
                            <span>{option.label}</span>
                            {option.value !== 'All' && <span className="count">{statusCount[option.value] ?? 0}</span>}
                        </button>
                    );
                })
                }
            </div>
            <MultiselectFilter
                label="Type"
                options={serviceTypeOptions}
                selectedList={typeFilter}
                onSelectionChange={setTypeFilter}
            />
            <MultiselectFilter
                label="Plan"
                options={servicePlanOptions}
                selectedList={planFilter}
                onSelectionChange={setPlanFilter}
            />
        </div>
    );
});

export default EcmFilters;